/**
 * PerformanceAccessories — custom element for the "performance_accessories" block.
 *
 * Supports one or more .pa-group sections inside a single element.
 * Each group has its own toggle, list, view-all/fold, and can carry
 * its own data-coupon / data-discount attributes.
 *
 * Discount modes (per group, set via data-discount-mode):
 *   fixed  — single discount %, price pre-rendered by Liquid (default)
 *   tiered — JS reads data-tiers JSON and applies the right tier based
 *            on how many units the user has selected (sum of all qty inputs
 *            across checked items in that group)
 *
 * UI flow:
 *  1. Each group has two toggle buttons: "Add …" / "No …" (neither active by default).
 *  2. "Add …" → reveals that group's accessories list.
 *  3. Each card is clickable; clicking checks it, shows variant select + qty selector.
 *  4. View All / Fold toggle shows/hides items beyond the initial count.
 *  5. On cart:item-added (theme event after main product add), POSTs all checked
 *     items across every group to /cart/add.json in one request.
 */

if (!customElements.get('performance-accessories')) {
  class PerformanceAccessories extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this._addingAccessories = false;
    }

    connectedCallback() {
      this._bindToggle();
      this._bindItems();
      this._bindViewAll();
      this._bindCartIntegration();
    }

    // ── Toggle buttons (one pair per .pa-group) ────────────────────────────

    _bindToggle() {
      this.querySelectorAll('.pa-group').forEach((group) => {
        const btnAdd  = group.querySelector('.pa-toggle-add');
        const btnNone = group.querySelector('.pa-toggle-none');
        const list    = group.querySelector('.pa-list');

        if (!btnAdd || !btnNone || !list) return;

        btnAdd.addEventListener('click', () => {
          if (btnAdd.classList.contains('is-active')) {
            btnAdd.classList.remove('is-active');
            list.setAttribute('hidden', '');
            return;
          }
          btnAdd.classList.add('is-active');
          btnNone.classList.remove('is-active');
          list.removeAttribute('hidden');
        });

        btnNone.addEventListener('click', () => {
          if (btnNone.classList.contains('is-active')) {
            btnNone.classList.remove('is-active');
            return;
          }
          btnNone.classList.add('is-active');
          btnAdd.classList.remove('is-active');
          list.setAttribute('hidden', '');
          this._deselectGroup(group);
        });
      });
    }

    // ── Item cards ─────────────────────────────────────────────────────────

    _bindItems() {
      this.querySelectorAll('.pa-item').forEach((item) => {
        const row = item.querySelector('.pa-item__row');
        if (row) {
          row.addEventListener('click', () => this._toggleItem(item));
        }

        const variantSelect = item.querySelector('.pa-variant-select');
        if (variantSelect) {
          variantSelect.addEventListener('change', (e) => {
            e.stopPropagation();
            this._onVariantChange(item, variantSelect);
          });
          variantSelect.addEventListener('click', (e) => e.stopPropagation());
        }

        const qtyMinus = item.querySelector('.pa-qty-btn[data-dir="-1"]');
        const qtyPlus  = item.querySelector('.pa-qty-btn[data-dir="1"]');
        const qtyInput = item.querySelector('.pa-qty-input');

        if (qtyMinus && qtyPlus && qtyInput) {
          qtyMinus.addEventListener('click', (e) => { e.stopPropagation(); this._adjustQty(item, -1); });
          qtyPlus.addEventListener('click',  (e) => { e.stopPropagation(); this._adjustQty(item,  1); });
          qtyInput.addEventListener('click',  (e) => e.stopPropagation());
          qtyInput.addEventListener('change', () => {
            this._updateQtyLabel(item);
            this._updateTierForItem(item);
          });
        }
      });
    }

    _toggleItem(item) {
      item.classList.contains('is-checked') ? this._uncheckItem(item) : this._checkItem(item);
    }

    _checkItem(item) {
      item.classList.add('is-checked');
      const extras = item.querySelector('.pa-item__extras');
      if (extras) extras.removeAttribute('hidden');
      const qtyInput = item.querySelector('.pa-qty-input');
      if (qtyInput) { qtyInput.value = 1; this._updateQtyLabel(item); }
      this._updateTierForItem(item);
    }

    _uncheckItem(item, skipTier = false) {
      item.classList.remove('is-checked');
      const extras = item.querySelector('.pa-item__extras');
      if (extras) extras.setAttribute('hidden', '');
      if (!skipTier) this._updateTierForItem(item);
    }

    _deselectGroup(group) {
      group.querySelectorAll('.pa-item.is-checked').forEach((item) => this._uncheckItem(item, true));
      // Single tier recalc after all items are deselected
      if (group.dataset.discountMode === 'tiered') this._updateTier(group);
    }

    // ── Variant change ──────────────────────────────────────────────────────

    _onVariantChange(item, select) {
      const option = select.selectedOptions[0];
      if (!option) return;

      const group = item.closest('.pa-group');
      const discountPct = parseInt(group?.dataset.discount, 10) || 0;
      const price = parseInt(option.dataset.price, 10);
      const discountedPrice = Math.round(price * (100 - discountPct) / 100);
      const available = option.dataset.available === 'true';
      const currencyCode = select.closest('[data-currency]')?.dataset.currency || '';

      const priceNow = item.querySelector('.pa-item__price-now');
      const priceWas = item.querySelector('.pa-item__price-was');
      if (priceNow) priceNow.textContent = this._formatMoney(discountedPrice) + (currencyCode ? ' ' + currencyCode : '');
      if (priceWas) {
        priceWas.textContent = this._formatMoney(price);
        if (discountPct > 0) priceWas.removeAttribute('hidden');
        else priceWas.setAttribute('hidden', '');
      }

      item.classList.toggle('is-unavailable', !available);
    }

    // ── Qty helpers ─────────────────────────────────────────────────────────

    _adjustQty(item, delta) {
      const input = item.querySelector('.pa-qty-input');
      if (!input) return;
      const min  = parseInt(input.min)   || 1;
      const max  = parseInt(input.max)   || 999;
      const next = Math.min(Math.max((parseInt(input.value) || 1) + delta, min), max);
      input.value = next;
      this._updateQtyLabel(item);
      const minus = item.querySelector('.pa-qty-btn[data-dir="-1"]');
      const plus  = item.querySelector('.pa-qty-btn[data-dir="1"]');
      if (minus) minus.disabled = next <= min;
      if (plus)  plus.disabled  = next >= max;
      this._updateTierForItem(item);
    }

    _updateQtyLabel(item) {
      const input = item.querySelector('.pa-qty-input');
      const label = item.querySelector('.pa-qty-label');
      if (!input || !label) return;
      const qty = parseInt(input.value) || 1;
      label.textContent = qty === 1 ? '1 item selected' : `${qty} items selected`;
    }

    // ── Tiered discount ─────────────────────────────────────────────────────

    /** Entry point: find the group and delegate if tiered mode is on */
    _updateTierForItem(item) {
      const group = item.closest('.pa-group');
      if (group && group.dataset.discountMode === 'tiered') {
        this._updateTier(group);
      }
    }

    /**
     * Recalculates the active tier for a group based on total selected quantity,
     * then updates prices and the tier progress UI.
     */
    _updateTier(group) {
      // 1. Count total selected quantity across all checked items in this group
      let totalQty = 0;
      group.querySelectorAll('.pa-item.is-checked').forEach((item) => {
        totalQty += parseInt(item.querySelector('.pa-qty-input')?.value, 10) || 1;
      });

      // 2. Find the highest tier whose minimum quantity is met
      let tiers = [];
      try { tiers = JSON.parse(group.dataset.tiers || '[]'); } catch (_) {}

      let activeTier = null;
      for (const tier of tiers) {
        if (totalQty >= tier.qty) activeTier = tier;
      }

      const pct    = activeTier ? activeTier.pct    : 0;
      const coupon = activeTier ? activeTier.coupon : '';

      // 3. Update group data attrs so the existing cart-submit coupon logic picks the right one
      group.dataset.discount = pct;
      group.dataset.coupon   = coupon;

      // 4. Recalculate displayed prices for all checked items
      this._recalcGroupPrices(group, pct);

      // 5. Update the tier indicator UI
      this._updateTierUI(group, totalQty, tiers, activeTier);
    }

    /**
     * Updates the price display for all checked items in the group.
     * Uses data-price on the item div as the base price (set by Liquid).
     * For multi-variant items, reads the selected option's data-price instead.
     */
    _recalcGroupPrices(group, pct) {
      const currencyCode = group.dataset.currency || '';
      group.querySelectorAll('.pa-item.is-checked').forEach((item) => {
        // Prefer the selected variant's price; fall back to item-level data-price
        const variantSelect = item.querySelector('.pa-variant-select');
        const basePrice = variantSelect
          ? parseInt(variantSelect.options[variantSelect.selectedIndex]?.dataset.price, 10)
          : parseInt(item.dataset.price, 10);

        if (!basePrice || isNaN(basePrice)) return;

        const discounted  = Math.round(basePrice * (100 - pct) / 100);
        const itemCurrency = item.dataset.currency || currencyCode;
        const priceNow    = item.querySelector('.pa-item__price-now');
        const priceWas    = item.querySelector('.pa-item__price-was');

        if (priceNow) {
          priceNow.textContent = this._formatMoney(discounted) + (itemCurrency ? ' ' + itemCurrency : '');
        }
        if (priceWas) {
          if (pct > 0) {
            priceWas.textContent = this._formatMoney(basePrice);
            priceWas.removeAttribute('hidden');
          } else {
            priceWas.setAttribute('hidden', '');
          }
        }
      });
    }

    /**
     * Updates the .pa-tiers chip UI: shows the bar, marks reached/active tiers.
     */
    _updateTierUI(group, totalQty, tiers, activeTier) {
      const tiersEl = group.querySelector('.pa-tiers');
      if (!tiersEl) return;

      // Bar is always visible — JS only updates which tiers are reached/active
      tiersEl.querySelectorAll('.pa-tier').forEach((el) => {
        const tierQty = parseInt(el.dataset.tierQty, 10);
        const isActive  = activeTier !== null && activeTier.qty === tierQty;
        const isReached = totalQty >= tierQty;
        el.classList.toggle('pa-tier--active',  isActive);
        el.classList.toggle('pa-tier--reached', isReached);
      });
    }

    // ── View All / Fold (one pair per .pa-group) ────────────────────────────

    _bindViewAll() {
      this.querySelectorAll('.pa-group').forEach((group) => {
        const viewAllBtn = group.querySelector('.pa-view-all');
        const foldBtn    = group.querySelector('.pa-fold');
        if (!viewAllBtn) return;

        viewAllBtn.addEventListener('click', () => {
          group.querySelectorAll('.pa-item--hidden').forEach((el) => {
            el.classList.remove('pa-item--hidden');
            el.dataset.wasHidden = '1';
          });
          viewAllBtn.setAttribute('hidden', '');
          if (foldBtn) foldBtn.removeAttribute('hidden');
        });

        if (foldBtn) {
          foldBtn.addEventListener('click', () => {
            group.querySelectorAll('.pa-item[data-was-hidden]').forEach((el) => {
              el.classList.add('pa-item--hidden');
              delete el.dataset.wasHidden;
            });
            foldBtn.setAttribute('hidden', '');
            viewAllBtn.removeAttribute('hidden');
          });
        }
      });
    }

    // ── Cart integration ────────────────────────────────────────────────────

    _bindCartIntegration() {
      // When the theme dispatches cart:item-added (after the main product is
      // successfully added), collect every checked item across ALL groups and
      // add them in a single /cart/add.json request.
      // _addingAccessories prevents our own fetch from re-triggering this.
      document.addEventListener('cart:item-added', () => {
        if (this._addingAccessories) return;

        const items = this._getSelectedItems();
        if (items.length > 0) {
          this._addAccessoriesToCart(items);
        }
      });
    }

    _getSelectedItems() {
      const items = [];
      this.querySelectorAll('.pa-item.is-checked').forEach((item) => {
        const variantSelect = item.querySelector('.pa-variant-select');
        const variantId = variantSelect
          ? parseInt(variantSelect.value, 10)
          : parseInt(item.dataset.variantId, 10);
        const qty = parseInt(item.querySelector('.pa-qty-input')?.value, 10) || 1;
        if (variantId && qty > 0) {
          items.push({ id: variantId, quantity: qty });
        }
      });
      return items;
    }

    async _addAccessoriesToCart(items) {
      this._addingAccessories = true;
      try {
        const response = await fetch('/cart/add.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });

        const data = await response.json();

        if (data.status && data.status !== 200) {
          console.error('Performance Accessories: cart error', data);
          return;
        }

        // Apply coupon code(s) from each group that has one configured.
        // In tiered mode, data-coupon is kept up to date by _updateTier().
        for (const group of this.querySelectorAll('.pa-group[data-coupon]')) {
          const code = group.dataset.coupon;
          if (code) await this._applyDiscount(code);
        }

        await this._refreshCartSections();
      } catch (err) {
        console.error('Performance Accessories: failed to add items', err);
      } finally {
        this._addingAccessories = false;
      }
    }

    async _applyDiscount(code) {
      try {
        await fetch(`/discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
      } catch (_) {
        // Non-critical — discount can still be applied at checkout
      }
    }

    async _refreshCartSections() {
      try {
        const sections = ['main-cart', 'cart-drawer', 'cart-bubble'];
        const res  = await fetch(`/cart?sections=${sections.join(',')}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const data = await res.json();

        const sectionMap = [
          { id: 'Cart',              section: 'main-cart',   selector: '.thb-cart-form' },
          { id: 'Cart-Drawer',       section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle',section: 'cart-bubble', selector: '.thb-item-count' },
        ];

        sectionMap.forEach(({ id, section, selector }) => {
          const container = document.getElementById(id);
          if (!container || !data[section]) return;
          const target = container.querySelector(selector) || container;
          const tmp = document.createElement('div');
          tmp.innerHTML = data[section];
          const src = tmp.querySelector(selector) || tmp;
          target.innerHTML = src.innerHTML;
        });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart-drawer:open');
        }
      } catch (err) {
        console.error('Performance Accessories: cart refresh failed', err);
      }
    }

    // ── Utility ─────────────────────────────────────────────────────────────

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const [int, dec] = amount.split('.');
      return `$${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
    }
  }

  customElements.define('performance-accessories', PerformanceAccessories);
}
